import { AppConfiguration } from "config";
import React, { useEffect, useRef, useState } from "react";
import { MetaTags } from 'react-meta-tags';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { Card, CardBody, CardHeader, CardTitle, Col, Container, Row, UncontrolledTooltip } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
    format
} from 'date-fns';
import { getCustomerIncidents, getTimeframeReportingData } from "store/admin/status-monitor/actions";
import { generateLast24Hours, generateLast30Days, generateLastHour } from "helpers/sf_utils";
import { STATUS_HISTORICAL_TIMEFRAME, STATUS_MONITOR_PROFILING_TAGS } from "helpers/sf_constants";
import CustomerIncidents from "./CustomerIncidents";
import CustomerIncidentModal from "./CustomerIncidentModal";
import SkeletonForStatusMonitor from "./SkeletonForStatusMonitor";
import { parseISO } from "date-fns";


const parseTimestamp = (timestamp) => {

    if (typeof timestamp === 'string') {
        return timestamp.split(/[-T:]/).map(Number);
    }
    else if (timestamp instanceof Date) {
        return [
            timestamp.getFullYear(),
            timestamp.getMonth() + 1, // getMonth() returns 0-11, so we add 1
            timestamp.getDate(),
            timestamp.getHours(),
            timestamp.getMinutes(),
            timestamp.getMilliseconds(),
            timestamp.getSeconds()
        ];
    }

};


const StatusMonitor = () => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [isHolisticView, setIsHolisticView] = useState(true);
    const [reportingView, setReportingView] = useState(STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR);
    const [reportingDates, setReportingDates] = useState({ startDate: null, endDate: null });

    const [integrationView, setIntegrationView] = useState(STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR);
    const [integrationDates, setIntegrationDates] = useState({ startDate: null, endDate: null });

    const [billingView, setBillingView] = useState(STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR);
    const [billingDates, setBillingDates] = useState({ startDate: null, endDate: null });

    const [customerIncidentModal, setCustomerIncidentModal] = useState(false)

    const [selectedModule, setSelectedModule] = useState(null);
    const [selectedTimestamp, setSelectedTimestamp] = useState(null);

    const dispatch = useDispatch();
    const isClickTriggered = useRef(false);

    const { customer_incidents, isLoading, error, reportingData, dashboardData, billingData, integrationData, analyticsData, campaignsData, adsData } = useSelector(state => ({
        reportingData: state.StatusMonitor.reportingData,
        dashboardData: state.StatusMonitor.dashboardData,
        billingData: state.StatusMonitor.billingData,
        integrationData: state.StatusMonitor.integrationData,
        analyticsData: state.StatusMonitor.analyticsData,
        campaignsData: state.StatusMonitor.campaignsData,
        adsData: state.StatusMonitor.adsData,
        customer_incidents: state.StatusMonitor.customer_incidents,
        isLoading: state.StatusMonitor.isLoading,
        error: state.StatusMonitor.error
    }));

    const fetchData = (profilingTagId, view, timestamp = null) => {

        let startDate, endDate;
        if (timestamp) {
            let year, month, day, hour, minute, milliseconds, seconds;
            if (typeof timestamp === 'string') {
                // If timestamp is a string, parse it
                [year, month, day, hour, minute, milliseconds, seconds] = parseTimestamp(timestamp);

                //[year, month, day, hour, minute, milliseconds, seconds] = timestamp.split(/[-T:]/).map(Number);
            } else if (timestamp instanceof Date) {
                [year, month, day, hour, minute, milliseconds, seconds] = parseTimestamp(timestamp);

            } else {
                console.error('Invalid timestamp format', timestamp);
                return; // Exit the function if the timestamp is invalid
            }

            if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS) {
                // Set start to the exact time of the timestamp and end to the last millisecond of the day
                startDate = new Date(Date.UTC(year, month, day, hour, minute, seconds, milliseconds)).toISOString();
                endDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString();
            } else if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS) {
                //startDate is teh start of the day of that timestamp starting from 00:00:00
                startDate = new Date(Date.UTC(year, month - 1, day, 7, 0, 0, 0)).toISOString();
                //endDate is the end of the day of that timestamp
                endDate = new Date(Date.UTC(year, month - 1, day + 1, 6, 59, 59, 999)).toISOString();
            } else if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR) {
                // Start of the hour (xx:00:00.000)
                startDate = new Date(Date.UTC(year, month - 1, day, hour, 0, 0, 0)).toISOString();
                // End of the hour (xx:59:59.999)
                endDate = new Date(Date.UTC(year, month - 1, day, hour, 59, 59, 999)).toISOString();
            }
        } else {
            const today = new Date();
            if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS) {
                endDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 6, 59, 59, 999)).toISOString();
                startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 30, 7, 0, 0, 0)).toISOString();
            } else if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR) {
                endDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), today.getUTCHours(), today.getUTCMinutes(), today.getUTCSeconds(), today.getUTCMilliseconds())).toISOString();
                startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), today.getUTCHours(), today.getUTCMinutes() - 59, today.getUTCSeconds(), today.getUTCMilliseconds())).toISOString();
            } else {
                endDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), today.getUTCHours(), 59, 59, 999)).toISOString();
                startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1, today.getUTCHours() + 1, 0, 0, 0)).toISOString();
            }
        }

        dispatch(getTimeframeReportingData({
            "start_date": startDate,
            "end_date": endDate,
            "profiling_tag_id": profilingTagId
        }));
    };


    // Function to format date in local timezone
    const formatToLocalTimezone = (dateString) => {
        // Parse the ISO date string to a Date object
        const date = parseISO(dateString);

        // Create an Intl.DateTimeFormat object for local formatting
        const localFormatter = new Intl.DateTimeFormat('default', {
            year: 'numeric',
            month: 'long',  // Full month name
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'  // Show timezone abbreviation
        });

        // Format the date to a readable string in the local timezone
        return localFormatter.format(date);
    };

    const calculateDatesBasedOnIncidents = (incidents) => {
        let dates = [];

        if (typeof incidents === 'string') {  // Handle single date string
            dates.push(new Date(incidents));
        } else if (Array.isArray(incidents)) {  // Handle array of objects
            dates = incidents.map(inc => {
                return new Date(inc.timeframe_start);
            });
        } else if (incidents && typeof incidents === 'object') {  // Handle single object
            dates.push(new Date(incidents.timeframe_start));
        }

        dates = dates.filter(date => !isNaN(date.valueOf())); // Filter out invalid dates
        if (dates.length > 0) {
            dates.sort((a, b) => a - b);
            return { startDate: dates[0], endDate: dates[dates.length - 1] };
        }
        return { startDate: new Date(), endDate: new Date() }; // Fallback to current date if all are invalid
    };


    const handleStatusBarClick = (view, timeFrame, profilingTagId) => {
        let newView;
        if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS) {
            newView = STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS;
        } else if (view === STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS) {
            newView = STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR;
        } else {
            return; // If it's already at 1 hour view, do nothing
        }

        isClickTriggered.current = true;
        setSelectedTimestamp(timeFrame);
        setSelectedModule(profilingTagId);

        let newDates = calculateDatesBasedOnIncidents(timeFrame)
        switch (profilingTagId) {
            case STATUS_MONITOR_PROFILING_TAGS.REPORTING:
            case STATUS_MONITOR_PROFILING_TAGS.DASHBOARD:
            case STATUS_MONITOR_PROFILING_TAGS.ANALYTICS:
            case STATUS_MONITOR_PROFILING_TAGS.CAMPAIGN_STATS:
            case STATUS_MONITOR_PROFILING_TAGS.AD_STATS:
                setReportingView(newView);
                setReportingDates(newDates);
                break;
            case STATUS_MONITOR_PROFILING_TAGS.INTEGRATION:
                setIntegrationView(newView);
                setIntegrationDates(newDates);
                break;
            case STATUS_MONITOR_PROFILING_TAGS.BILLING:
                setBillingView(newView);
                setBillingDates(newDates);
                break;
        }
    };

    useEffect(() => {
        if (!isClickTriggered.current) {
            fetchData(STATUS_MONITOR_PROFILING_TAGS.REPORTING, reportingView);
            fetchData(STATUS_MONITOR_PROFILING_TAGS.DASHBOARD, reportingView);
            fetchData(STATUS_MONITOR_PROFILING_TAGS.ANALYTICS, reportingView);
            fetchData(STATUS_MONITOR_PROFILING_TAGS.CAMPAIGN_STATS, reportingView);
            fetchData(STATUS_MONITOR_PROFILING_TAGS.AD_STATS, reportingView);
        }
    }, [dispatch, reportingView]);

    useEffect(() => {
        if (!isClickTriggered.current) {
            fetchData(STATUS_MONITOR_PROFILING_TAGS.INTEGRATION, integrationView);
        }
    }, [dispatch, integrationView]);

    useEffect(() => {
        if (!isClickTriggered.current) {
            fetchData(STATUS_MONITOR_PROFILING_TAGS.BILLING, billingView);
        }
    }, [dispatch, billingView]);

    useEffect(() => {
        if (isClickTriggered.current && selectedModule && selectedTimestamp) {
            switch (selectedModule) {
                case STATUS_MONITOR_PROFILING_TAGS.REPORTING:
                case STATUS_MONITOR_PROFILING_TAGS.DASHBOARD:
                case STATUS_MONITOR_PROFILING_TAGS.ANALYTICS:
                case STATUS_MONITOR_PROFILING_TAGS.CAMPAIGN_STATS:
                case STATUS_MONITOR_PROFILING_TAGS.AD_STATS:
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.REPORTING, reportingView, selectedTimestamp);
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.DASHBOARD, reportingView, selectedTimestamp);
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.ANALYTICS, reportingView, selectedTimestamp);
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.CAMPAIGN_STATS, reportingView, selectedTimestamp);
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.AD_STATS, reportingView, selectedTimestamp);
                    break;
                case STATUS_MONITOR_PROFILING_TAGS.INTEGRATION:
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.INTEGRATION, integrationView, selectedTimestamp);
                    break;
                case STATUS_MONITOR_PROFILING_TAGS.BILLING:
                    fetchData(STATUS_MONITOR_PROFILING_TAGS.BILLING, billingView, selectedTimestamp);
                    break;
            }
            isClickTriggered.current = false;
        }
    }, [selectedModule, selectedTimestamp, reportingView, integrationView, billingView]);


    useEffect(() => {
        dispatch(getCustomerIncidents());
    }, []);


    const createIncidentsMap = (incidents, view) => {
        if (!Array.isArray(incidents)) {
            return {};
        }

        return incidents.reduce((acc, incident) => {
            const date = new Date(incident.timeframe_start);
            if (!isNaN(date)) { // Check if date is valid
                acc[format(date, view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
                    ? "yyyy-MM-dd"
                    : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
                        ? "mm"
                        : "HH")] = incident;
            } else {
                console.error('Invalid date:', incident.timeframe_start);
            }
            return acc;
        }, {});
    };



    const last30Days = generateLast30Days();
    const last24Hours = generateLast24Hours();
    const last1Hour = generateLastHour();

    const reportingIncidentsMap = createIncidentsMap(reportingData, reportingView);
    const dashboardIncidentsMap = createIncidentsMap(dashboardData, reportingView);
    const analyticsIncidentsMap = createIncidentsMap(analyticsData, reportingView);
    const campaignsIncidentsMap = createIncidentsMap(campaignsData, reportingView);
    const adsIncidentsMap = createIncidentsMap(adsData, reportingView);

    const integrationIncidentsMap = createIncidentsMap(integrationData, integrationView);
    const billingIncidentsMap = createIncidentsMap(billingData, billingView);

    const calculateErrorPercentage = (totalErrors, totalRequests) => {
        return (totalErrors / totalRequests) * 100.0;
    };

    const getStatusClass = (incident) => {
        if (!incident || !incident.total_requests) {
            return 'status-bar-success';
        }
        const errorPercentage = calculateErrorPercentage(incident?.total_errors, incident?.total_requests);
        if (errorPercentage > 5) {
            return 'status-bar-failure';
        } else if (errorPercentage > 0.0 && errorPercentage <= 5) {
            return 'bg-warning';
        }
        return 'status-bar-success';
    };
    
    const renderStatusBars = (incidentsMap, profilingTagId, view) => {
    const timeFrames = view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
        ? last30Days
        : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
        ? last1Hour
        : last24Hours;

    return (
        <div className="status-container overflow-auto p-3 w-100">
        {timeFrames.map((timeFrame, i) => {
            const formattedDate = format(timeFrame, view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
            ? "dd"
            : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
                ? "mm"
                : "h a");

            const incident = incidentsMap[format(timeFrame, view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
            ? "yyyy-MM-dd"
            : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
                ? "mm"
                : "HH")];

            const isIncident = !!incident;
            const statusClass = getStatusClass(incident);

            return (
            <div
                key={i}
                className="status-bar-with-date d-flex flex-column align-items-center mx-2" // Increased spacing to mx-2
                onClick={() => handleStatusBarClick(view, incident?.timeframe_start, profilingTagId)}
            >
                <div
                id={`status-bar-${i}-${profilingTagId}`}
                className={`status-bar ${statusClass}`}
                onMouseEnter={() => isIncident && setHoveredIndex(i)}
                onMouseLeave={() => isIncident && setHoveredIndex(null)}
                />
                <div className="date-label small">{formattedDate}</div>
            </div>
            );
        })}
        </div>
    );
    };const renderTooltips = (incidentsMap, profilingTagId, view) => {
    const timeFrames = view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
        ? last30Days
        : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
        ? last1Hour
        : last24Hours;

    return timeFrames.map((timeFrame, i) => {
        const formattedDate = format(timeFrame, view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
        ? "yyyy-MM-dd"
        : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
            ? "mm"
            : "HH");

        const incident = incidentsMap[formattedDate];
        if (incident) {
        const errorPercentage = ((incident.total_errors / incident.total_requests) * 100).toFixed(2);
        return (
            <UncontrolledTooltip key={i} placement="top" target={`status-bar-${i}-${profilingTagId}`}>
            <div className="text-white">
                {incident.total_errors} Error{incident.total_errors > 1 ? 's' : ''} out of {incident.total_requests} total requests - {errorPercentage}% failure
            </div>
            <div className="text-white">
                <br />
                {formatToLocalTimezone(incident.timeframe_start)}
            </div>
            </UncontrolledTooltip>
        );
        }
        return null;
    });
    };
    const TimeframeRow = ({ view, startDate, endDate }) => {
        const formatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        const formattedStartDate = startDate ? new Date(startDate).toLocaleDateString('en-US', formatOptions) : null;
        const formattedEndDate = endDate ? new Date(endDate).toLocaleDateString('en-US', formatOptions) : null;

        const formattedStartDateWithTime = startDate ? new Date(startDate).toLocaleString('en-US', { ...formatOptions, hour: '2-digit', minute: '2-digit' }) : null;
        const formattedEndDateWithTime = endDate ? new Date(endDate).toLocaleString('en-US', { ...formatOptions, hour: '2-digit', minute: '2-digit' }) : null;

        return (
            <Row className="d-flex justify-content-between align-items-center px-1">
                <Col md='auto' className="small">
                    {formattedStartDate
                        ? (view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR ? formattedStartDateWithTime : formattedStartDate)
                        : (view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
                            ? '30 days ago'
                            : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
                                ? '1 hour ago'
                                : '24 hours ago')}
                </Col>
                <Col md='10' className="w-50">
                    <hr />
                </Col>
                <Col md='auto' className="small">
                    {formattedEndDate
                        ? (view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR ? formattedEndDateWithTime : formattedEndDate)
                        : (view === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS
                            ? 'Today'
                            : view === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR
                                ? 'Now'
                                : 'Now')}
                </Col>
            </Row>
        );
    };

    const toggleView = () => setIsHolisticView(!isHolisticView);

    const onCloseCustomerIncident = () => {
        setCustomerIncidentModal(false)
    }

    const saveCustomerIncidentModal = () => {
        setCustomerIncidentModal(true)
    }

    return (
        <React.Fragment>
            <CustomerIncidentModal
                show={customerIncidentModal}
                onSaveCustomerIncident={() => setCustomerIncidentModal(false)}
                onCloseClick={onCloseCustomerIncident}
            />
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>Status Monitor | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="" breadcrumbItem="Status Monitor" />

                    {/* Incidents reported on slack starts */}
                    {isLoading ? <SkeletonForStatusMonitor /> :
                        <CustomerIncidents customer_incidents={customer_incidents} triggerCustomerIncidentModalFunc={saveCustomerIncidentModal} />
                    }
                    {/* Incidents reported on slack ends */}

                    {/* Reporting incidents starts */}
                    {isLoading ? <SkeletonForStatusMonitor /> :
                        <Row className="d-flex justify-content-center">
                            <Col md={10}>
                                <Card>
                                    <CardBody>
                                        <CardHeader className="d-flex justify-content-between">
                                            <CardTitle className="d-flex align-items-center gap-1">Reporting  <a href='#' onClick={toggleView}><i className="bx bx-usb text-primary align-middle" /></a></CardTitle>
                                            <div className="d-flex gap-2">
                                                <a href='#' className={`${reportingView === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setReportingView(STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS)
                                                        setReportingDates({ startDate: null, endDate: null });
                                                    }
                                                    }>Last 30 days</a>
                                                <a href='#' className={`${reportingView === STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setReportingView(STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS)
                                                        setReportingDates({ startDate: null, endDate: null });
                                                    }
                                                    }>Last 24 Hrs Status</a>
                                                <a href='#' className={`${reportingView === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setReportingView(STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR)
                                                        setReportingDates({ startDate: null, endDate: null });
                                                    }
                                                    }>Last 1 Hour</a>
                                            </div>
                                        </CardHeader>
                                        {isHolisticView ? (
                                            <div className="status-container p-3">
                                                {renderStatusBars(reportingIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.REPORTING, reportingView)}
                                                {renderTooltips(reportingIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.REPORTING, reportingView)}
                                            </div>
                                        ) : (
                                            <>
                                                <CardBody>
                                                    <CardTitle>Dashboard</CardTitle>
                                                    <div className="status-container">
                                                        {renderStatusBars(dashboardIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.DASHBOARD, reportingView)}
                                                        {renderTooltips(dashboardIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.DASHBOARD, reportingView)}
                                                    </div>
                                                </CardBody>
                                                <CardBody>
                                                    <CardTitle>Analytics </CardTitle>
                                                    <div className="status-container">
                                                        {renderStatusBars(analyticsIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.ANALYTICS, reportingView)}
                                                        {renderTooltips(analyticsIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.ANALYTICS, reportingView)}
                                                    </div>
                                                </CardBody>
                                                <CardBody>
                                                    <CardTitle>Campaigns </CardTitle>
                                                    <div className="status-container">
                                                        {renderStatusBars(campaignsIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.CAMPAIGN_STATS, reportingView)}
                                                        {renderTooltips(campaignsIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.CAMPAIGN_STATS, reportingView)}
                                                    </div>
                                                </CardBody>
                                                <CardBody>
                                                    <CardTitle>Ads </CardTitle>
                                                    <div className="status-container">
                                                        {renderStatusBars(adsIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.AD_STATS, reportingView)}
                                                        {renderTooltips(adsIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.AD_STATS, reportingView)}
                                                    </div>
                                                </CardBody>
                                            </>
                                        )}
                                        <TimeframeRow view={reportingView} startDate={reportingDates.startDate} endDate={reportingDates.endDate} />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    }
                    {/* Reporting incidents ends */}

                    {/* Integration incidents starts */}
                    {isLoading ? <SkeletonForStatusMonitor /> :
                        <Row className="d-flex justify-content-center">
                            <Col md={10}>
                                <Card>
                                    <CardBody>
                                        <CardHeader className="d-flex justify-content-between">
                                            <CardTitle className="d-flex align-items-center gap-1">Integration </CardTitle>
                                            <div className="d-flex gap-2">
                                                <a href='#' className={`${integrationView === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setIntegrationView(STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS)
                                                        setReportingDates({ startDate: null, endDate: null });
                                                    }
                                                    }>Last 30 days</a>
                                                <a href='#' className={`${integrationView === STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setIntegrationView(STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS)
                                                        setReportingDates({ startDate: null, endDate: null });
                                                    }
                                                    }>Last 24 Hrs Status</a>
                                                <a href='#' className={`${integrationView === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setIntegrationView(STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR)
                                                        setReportingDates({ startDate: null, endDate: null });
                                                    }
                                                    }>Last 1 Hour</a>
                                            </div>
                                        </CardHeader>
                                        <div className="status-container p-3">
                                            {renderStatusBars(integrationIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.INTEGRATION, integrationView)}
                                            {renderTooltips(integrationIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.INTEGRATION, integrationView)}
                                        </div>
                                        <TimeframeRow startDate={integrationDates.startDate} endDate={integrationDates.endDate} view={integrationView} />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    }
                    {/* Integration incidents ends */}

                    {/* Billing incidents starts */}
                    {isLoading ? <SkeletonForStatusMonitor /> :
                        <Row className="d-flex justify-content-center">
                            <Col md={10}>
                                <Card>
                                    <CardBody>
                                        <CardHeader className="d-flex justify-content-between">
                                            <CardTitle className="d-flex align-items-center gap-1">Billing </CardTitle>
                                            <div className="d-flex gap-2">
                                                <a href='#' className={`${billingView === STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setBillingView(STATUS_HISTORICAL_TIMEFRAME.LAST_30_DAYS)
                                                        setBillingDates({ startDate: null, endDate: null })
                                                    }
                                                    }>Last 30 days</a>
                                                <a href='#' className={`${billingView === STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setBillingView(STATUS_HISTORICAL_TIMEFRAME.LAST_24_HOURS)
                                                        setBillingDates({ startDate: null, endDate: null })
                                                    }
                                                    }>Last 24 Hrs Status</a>
                                                <a href='#' className={`${billingView === STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR ?
                                                    'text-black' : 'text-primary font-weight-semibold'}`}
                                                    onClick={() => {
                                                        setBillingView(STATUS_HISTORICAL_TIMEFRAME.LAST_1_HOUR)
                                                        setBillingDates({ startDate: null, endDate: null })
                                                    }
                                                    }>Last 1 Hour</a>
                                            </div>
                                        </CardHeader>
                                        <div className="status-container p-3">
                                            {renderStatusBars(billingIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.BILLING, billingView)}
                                            {renderTooltips(billingIncidentsMap, STATUS_MONITOR_PROFILING_TAGS.BILLING, billingView)}
                                        </div>
                                        <TimeframeRow startDate={billingDates.startDate} endDate={billingDates.endDate} view={billingView} />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    }
                    {/* Billing incidents ends */}
                </Container>
            </div>
        </React.Fragment>
    );
}

export default StatusMonitor;